import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchUsers,
  grantPremiumSubscription,
  updateUserStatus,
} from "../../redux/slices/usersSlice";
import { RootState, AppDispatch } from "../../redux/store";
import Header from "../../components/Header";
import ExpandButton from "../../components/ExpandButton";
import Loading from "../../components/Loading";
import Pagination from "../../components/Pagination";
import Select from "react-select";
import debounce from "lodash/debounce";
import { getSubscriptionCount, searchUsers } from "../../services/userService";
import { getSubscriptionStatusLabel } from "../../utils/subscriptionStatusLabel";
import ErrorMessage from "../../components/ErrorMessage";
import SubscriptionDropdown from "../../components/SubcsriptionDropdown";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import api from "../../services/api";
import { upperCase } from "lodash";

interface FilterOption {
  label: string;
  value: string;
}

const UserDetails: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { users, loading, error, totalPages, totalUsers } = useSelector(
    (state: RootState) => state.users,
  );
  const [openMenu, setOpenMenu] = useState<number | null>(null);
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [disableBlocked, setDisableBlocked] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState(1);

  const [selectedNames, setSelectedNames] = useState<FilterOption[]>([]);
  const [selectedEmails, setSelectedEmails] = useState<FilterOption[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<FilterOption[]>([]);

  const [nameOptions, setNameOptions] = useState<FilterOption[]>([]);
  const [emailOptions, setEmailOptions] = useState<FilterOption[]>([]);
  const [roleOptions] = useState<FilterOption[]>([
    { label: "User", value: "user" },
    { label: "Admin", value: "admin" },
    { label: "Superadmin", value: "superadmin" },
  ]);

  const [isLoadingNames, setIsLoadingNames] = useState(false);
  const [isLoadingEmails, setIsLoadingEmails] = useState(false);

  const [subscriptionCounts, setSubscriptionsCount] = useState([]);

  const [isSubscriptionInfoShow, setIsSubscriptionInfoShow] = useState(true);

  const [prevFilters, setPrevFilters] = useState({
    names: selectedNames,
    emails: selectedEmails,
    roles: selectedRoles,
  });

  const countSubscriptions = async () => {
    const resp = await getSubscriptionCount();
    setSubscriptionsCount(resp);
  };

  useEffect(() => {
    countSubscriptions();
    const filters = {
      name: selectedNames.map((option) => option.value),
      email: selectedEmails.map((option) => option.value),
      role: selectedRoles.map((option) => option.value),
    };

    const filtersChanged =
      selectedNames !== prevFilters.names ||
      selectedEmails !== prevFilters.emails ||
      selectedRoles !== prevFilters.roles;

    setPrevFilters({
      names: selectedNames,
      emails: selectedEmails,
      roles: selectedRoles,
    });

    const pageToUse = filtersChanged ? 1 : currentPage;

    dispatch(
      fetchUsers({
        page: pageToUse,
        limit: 10,
        ...filters,
      }),
    );
  }, [
    selectedNames,
    selectedEmails,
    selectedRoles,
    currentPage,
    dispatch,
    prevFilters.names,
    prevFilters.emails,
    prevFilters.roles,
  ]);

  const debouncedNameSearch = debounce(async (inputValue: string) => {
    if (inputValue) {
      setIsLoadingNames(true);
      try {
        const results = await searchUsers("name", inputValue);
        setNameOptions(results.map((name) => ({ label: name, value: name })));
      } catch (error) {
        console.error("Error searching names:", error);
      } finally {
        setIsLoadingNames(false);
      }
    } else {
      setNameOptions([]);
    }
  }, 300);

  const debouncedEmailSearch = debounce(async (inputValue: string) => {
    if (inputValue) {
      setIsLoadingEmails(true);
      try {
        const results = await searchUsers("email", inputValue);
        console.log(results);
        const options = results.map((email) => ({
          label: email ? String(email) : "",
          value: email != null ? String(email) : "",
        }));

        console.log(options);
        setEmailOptions(options);
      } catch (error) {
        console.error("Error searching names:", error);
      } finally {
        setIsLoadingEmails(false);
      }
    } else {
      setEmailOptions([]);
    }
  }, 300);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const currentUserRole = localStorage.getItem("role");

  const toggleMenu = (userId: number, userRole: string) => {
    setOpenMenu((prev) => (prev === userId ? null : userId));
    console.log(currentUserRole, userRole);

    if (currentUserRole === "admin" && userRole === "superadmin") {
      console.log("admin");

      setDisableBlocked(true);
    } else {
      setDisableBlocked(false);
    }
  };

  const toggleExpandRow = (userId: number) => {
    setExpandedRow((prev) => (prev === userId ? null : userId));
  };

  const handleStatusChange = (
    userId: number,
    isBlocked: boolean,
    userRole: string,
  ) => {
    if (currentUserRole === "admin" && userRole === "superadmin") {
      setErrorMessage(
        "Admin users cannot change the status of Superadmin users.",
      );
      return;
    }

    dispatch(updateUserStatus({ userId, isBlocked }));
    setOpenMenu(null);
  };

  const handleSubscriptionChange = (
    userId: number,
    plan: "monthly" | "yearly" | "inactive",
  ) => {
    if (plan === "inactive") {
      dispatch(grantPremiumSubscription({ userId, isPremium: false, plan }));
      console.log(plan);
    } else {
      dispatch(grantPremiumSubscription({ userId, isPremium: true, plan }));
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toISOString().split("T")[0];
  };

  const sortedUsers = [...users].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  const getPlanLabel = (subscriptionType: string | null | undefined) => {
    switch (subscriptionType) {
      case "paid-1-month":
        return "Month";
      case "paid-3-months":
        return "Quarter";
      case "paid-1-year":
        return "Year";
      case "paid-1-year-together":
        return "Year (Together)";
      case "paid-1-year-special":
        return "Year (Special)";
      case "free-1-month":
        return "Free Month";
      case "free-1-year":
        return "Free Year";
      case "free-trial":
        return "Free Trial";
      default:
        return null;
    }
  };

  const getSubscriptionLabel = (subscriptionType: string) => {
    console.log("subscriptionType", subscriptionType);

    switch (subscriptionType) {
      case "active":
        return "Active";
      case "free_1_month":
        return "Free 1 Month";
      case "monthly":
        return "Free 1 Month";
      case "free_1_year":
        return "Free 1 Year";
      case "yearly":
        return "Free 1 Year";
      case "inactive":
        return "Inactive";
      default:
        return "Inactive";
    }
  };

  const exportUsersToExcel = async () => {
    try {
      const response = await api.get(`/auth/export`);

      if (response.status !== 200) throw new Error("Failed to fetch users");

      const users = response.data;

      const data = users.map((user: any) => ({
        ID: user.id,
        Name: user.name,
        Email: user.email,
        Role: user.role,
        Status: user.isBlocked ? "Blocked" : "Active",

        Subscription: user.typeOfSubscription || "Inactive",
        "Date Added": user.createdAt?.split("T")[0] || "N/A",
      }));

      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Users");

      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      saveAs(blob, "users.xlsx");
    } catch (error) {
      console.error("Export error:", error);
    }
  };

  const exportUsersToCSV = async () => {
    try {
      const response = await api.get(`/auth/export`);

      if (response.status !== 200) {
        throw new Error("Failed to fetch users");
      }

      const users = response.data;

      const data = users.map((user: any) => ({
        Name: user.name,
        Email: user.email,
      }));

      const worksheet = XLSX.utils.json_to_sheet(data);

      const csv = XLSX.utils.sheet_to_csv(worksheet, {
        FS: ",",
        RS: "\n",
      });

      const blob = new Blob([csv], {
        type: "text/csv;charset=utf-8;",
      });

      saveAs(blob, "users.csv");
    } catch (error) {
      console.error("Export CSV error:", error);
    }
  };

  useEffect(() => {
    const getSubInfo = () => {
      const subInfo = localStorage.getItem("isSubscriptionInfoShow");
      if (subInfo === "false") {
        setIsSubscriptionInfoShow(false);
      }
    };
    getSubInfo();
  }, []);

  const handleSubscriptionInfoShow = () => {
    localStorage.setItem(
      "isSubscriptionInfoShow",
      String(!isSubscriptionInfoShow),
    );
    setIsSubscriptionInfoShow(!isSubscriptionInfoShow);
  };

  return (
    <div className="p-4 md:p-6 bg-gray-100 min-h-screen">
      <Header title="Users" />
      <button
        onClick={() => handleSubscriptionInfoShow()}
        className="px-4 py-1 bg-gray-400 text-white rounded hover:bg-gray-500 text-sm"
      >
        {isSubscriptionInfoShow
          ? "Hide subscription info"
          : "Show subscription info"}
      </button>
      {isSubscriptionInfoShow && (
        <div className="mb-4 bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full border-collapse">
            <thead className="bg-gray-200">
              <tr>
                {subscriptionCounts.map((s: any) => {
                  return (
                    <th
                      className="py-1 px-4 text-center"
                      key={s.subscriptionType}
                    >
                      {upperCase(s.subscriptionType)}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              <tr>
                {subscriptionCounts.map((s: any) => {
                  return (
                    <td
                      className="py-1 px-4 text-sm text-center"
                      key={s.subscriptionType}
                    >
                      {s.count}
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      )}
      <div className="flex justify-end mb-4">
        <button
          onClick={() => exportUsersToExcel()}
          className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
        >
          Export to XLSX
        </button>
        <button
          onClick={() => exportUsersToCSV()}
          className="mx-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
        >
          Export to CSV
        </button>
      </div>
      <div className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-4">
        <Select
          isMulti
          options={nameOptions}
          value={selectedNames}
          onChange={(selected) => setSelectedNames(selected as FilterOption[])}
          onInputChange={(input) => {
            if (typeof input === "string") {
              debouncedNameSearch(input);
            }
          }}
          placeholder="Filter by Name"
          className="basic-multi-select"
          classNamePrefix="select"
          isLoading={isLoadingNames}
          isClearable
        />
        <Select
          isMulti
          options={emailOptions}
          value={selectedEmails}
          onChange={(selected) => setSelectedEmails(selected as FilterOption[])}
          onInputChange={(input) => {
            if (typeof input === "string") {
              debouncedEmailSearch(input);
            }
          }}
          placeholder="Filter by Email"
          className="basic-multi-select"
          classNamePrefix="select"
          isLoading={isLoadingEmails}
          isClearable
        />
        <Select
          isMulti
          options={roleOptions}
          value={selectedRoles}
          onChange={(selected) => setSelectedRoles(selected as FilterOption[])}
          placeholder="Filter by Role"
          className="basic-multi-select"
          classNamePrefix="select"
          isClearable
        />
      </div>
      <div className="mt-6 bg-white rounded-lg shadow overflow-x-auto">
        {loading ? (
          <Loading message="Loading Users..." />
        ) : (
          <>
            <table className="min-w-full border-collapse">
              <thead className="bg-gray-200">
                <tr>
                  <th className="py-2 px-4 text-left">ID</th>
                  <th className="py-2 px-4 text-left">Name</th>
                  <th className="py-2 px-4 text-left">Email</th>
                  <th className="py-2 px-4 text-left">Date Added</th>
                  <th className="py-2 px-4 text-left">Role</th>
                  <th className="py-2 px-4 text-left">Age</th>
                  <th className="py-2 px-4 text-left">Gender</th>
                  {isSubscriptionInfoShow && (
                    <th className="py-2 px-4 text-left">
                      Current Subscription
                    </th>
                  )}
                  <th className="py-2 px-4 text-left">Subscription</th>
                  <th className="py-2 px-4 text-left">Status</th>
                  <th className="py-2 px-4 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedUsers.map((user: any) => (
                  <React.Fragment key={user.id}>
                    <tr className="border-t">
                      <td className="py-3 px-4 text-sm whitespace-nowrap">
                        {user.id || "N/A"}
                      </td>
                      <td className="py-3 px-4 text-sm whitespace-nowrap">
                        {user.name || "N/A"}
                      </td>
                      <td className="py-3 px-4 text-sm whitespace-nowrap">
                        {user.email}
                      </td>
                      <td className="py-3 px-4 text-sm whitespace-nowrap">
                        {formatDate(user.createdAt)}
                      </td>
                      <td className="py-3 px-4 text-sm whitespace-nowrap">
                        {user.role}
                      </td>
                      <td className="py-3 px-4 text-sm whitespace-nowrap">
                        {user.questionnaireAnswers.find(
                          (q: any) => q.questionnaire.profileField === "age",
                        )?.answer || "N/A"}
                      </td>
                      <td className="py-3 px-4 text-sm whitespace-nowrap">
                        {user.questionnaireAnswers.find(
                          (q: any) => q.questionnaire.profileField === "gender",
                        )?.selectedOptions[0]?.label || "N/A"}
                      </td>
                      {isSubscriptionInfoShow && (
                        <td className="py-3 px-4 text-sm whitespace-nowrap">
                          {(() => {
                            const plan = getPlanLabel(
                              user.currentSubscription?.type,
                            );
                            const status = getSubscriptionStatusLabel(
                              user.subscriptionStatus,
                            );
                            return plan ? `${plan} · ${status}` : status;
                          })()}
                          {user.subscriptionExpiresAt &&
                            (user.subscriptionStatus === "cancelled" ||
                              user.subscriptionStatus === "billing_issue") && (
                              <span className="text-gray-400">
                                {" "}
                                — until {formatDate(user.subscriptionExpiresAt)}
                              </span>
                            )}
                        </td>
                      )}
                      <td className="py-3 px-4 text-sm whitespace-nowrap">
                        <SubscriptionDropdown
                          currentSubscription={getSubscriptionLabel(
                            user.typeOfSubscription || "N/A",
                          )}
                          onChange={(newSubscription) =>
                            handleSubscriptionChange(
                              user.id,
                              newSubscription as "monthly" | "yearly",
                            )
                          }
                        />
                      </td>

                      <td className="py-3 px-4 text-sm whitespace-nowrap relative">
                        <button
                          className={`flex items-center gap-1 px-2 py-1 text-xs rounded w-24 ${
                            user.isBlocked
                              ? "bg-red-100 text-red-600"
                              : user.isVerified
                                ? "bg-green-100 text-green-600"
                                : "bg-gray-100 text-gray-600"
                          } hover:bg-gray-200 transition-all`}
                          onClick={() => toggleMenu(user.id, user.role)}
                        >
                          {user.isBlocked
                            ? "Blocked"
                            : user.isVerified
                              ? "Active"
                              : "Pending"}
                          <span className="text-gray-500 ml-1">&#9662;</span>
                        </button>

                        {openMenu === user.id && (
                          <div className="absolute mt-2 w-24 bg-white border border-gray-300 rounded shadow-lg z-10 right-0">
                            <button
                              onClick={() =>
                                handleStatusChange(user.id, false, user.role)
                              }
                              className="block w-full px-3 py-2 text-xs text-green-600 hover:bg-gray-100"
                            >
                              Active
                            </button>
                            <button
                              onClick={() =>
                                handleStatusChange(user.id, true, user.role)
                              }
                              className={`block w-full px-3 py-2 text-xs ${
                                disableBlocked
                                  ? "text-gray-400 cursor-not-allowed"
                                  : "text-red-600 hover:bg-gray-100"
                              }`}
                              disabled={disableBlocked}
                            >
                              Blocked
                            </button>
                          </div>
                        )}
                      </td>

                      <td className="py-3 px-2 text-sm whitespace-nowrap">
                        <ExpandButton
                          text={expandedRow === user.id ? "Less" : "More"}
                          icon={
                            expandedRow === user.id ? (
                              <span className="text-lg font-bold">-</span>
                            ) : (
                              <span className="text-lg font-bold">+</span>
                            )
                          }
                          onClick={() => toggleExpandRow(user.id)}
                          className="w-16 px-2 py-1 text-red-600 border border-red-600 rounded hover:bg-gray-100 text-xs"
                        />
                      </td>
                    </tr>
                    {expandedRow === user.id && (
                      <tr>
                        <td colSpan={10} className="py-4">
                          <div className="bg-white shadow-xl rounded-lg p-2">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                              <div className="border-b border-gray-300 pb-2">
                                <p className="text-xs text-gray-500">
                                  Current Weight:
                                </p>
                                <p className="text-sm font-medium text-gray-800">
                                  {user.questionnaireAnswers.find(
                                    (q: any) =>
                                      q.questionnaire.question ===
                                      "What's Your Current Weight?",
                                  )?.answer || "N/A"}{" "}
                                  kg
                                </p>
                              </div>
                              <div className="border-b border-gray-300 pb-2">
                                <p className="text-xs text-gray-500">
                                  Target Weight:
                                </p>
                                <p className="text-sm font-medium text-gray-800">
                                  {user.questionnaireAnswers.find(
                                    (q: any) =>
                                      q.questionnaire.question ===
                                      "What's Your Target Weight?",
                                  )?.answer || "N/A"}{" "}
                                  kg
                                </p>
                              </div>
                              <div className="border-b border-gray-300 pb-2">
                                <p className="text-xs text-gray-500">Height:</p>
                                <p className="text-sm font-medium text-gray-800">
                                  {user.questionnaireAnswers.find(
                                    (q: any) =>
                                      q.questionnaire.question ===
                                      "What's Your Current Height?",
                                  )?.answer || "N/A"}{" "}
                                  cm
                                </p>
                              </div>
                              <div className="border-b border-gray-300 pb-2">
                                <p className="text-xs text-gray-500">
                                  Current Fitness Level:
                                </p>
                                <p className="text-sm font-medium text-gray-800">
                                  {user.questionnaireAnswers.find(
                                    (q: any) =>
                                      q.questionnaire.question ===
                                      "What's Your Current Fitness Level?",
                                  )?.selectedOptions[0]?.label || "N/A"}
                                </p>
                              </div>
                              <div className="border-b border-gray-300 pb-2">
                                <p className="text-xs text-gray-500">
                                  Minutes for a train:
                                </p>
                                <p className="text-sm font-medium text-gray-800">
                                  {user.questionnaireAnswers.find(
                                    (q: any) =>
                                      q.questionnaire.question ===
                                      "How Many Minutes Do You Train For?",
                                  )?.selectedOptions[0]?.label || "N/A"}
                                </p>
                              </div>
                              <div className="border-b border-gray-300 pb-2">
                                <p className="text-xs text-gray-500">
                                  Fitness Mood:
                                </p>
                                <p className="text-sm font-medium text-gray-800">
                                  {user.questionnaireAnswers.find(
                                    (q: any) =>
                                      q.questionnaire.question ===
                                      "How Would You Describe Your Fitness Mood?",
                                  )?.selectedOptions[0]?.label || "N/A"}
                                </p>
                              </div>
                              <div className="border-b border-gray-300 pb-2">
                                <p className="text-xs text-gray-500">
                                  Fitness Goal:
                                </p>
                                <p className="text-sm font-medium text-gray-800">
                                  {user.questionnaireAnswers
                                    .find((q: any) =>
                                      q.questionnaire.question.includes(
                                        "Main Fitness Goal",
                                      ),
                                    )
                                    ?.selectedOptions.map(
                                      (goal: any) => goal.label,
                                    )
                                    .join(", ") || "N/A"}
                                </p>
                              </div>
                              <div className="border-b border-gray-300 pb-2">
                                <p className="text-xs text-gray-500">
                                  Body Type:
                                </p>
                                <p className="text-sm font-medium text-gray-800">
                                  {user.questionnaireAnswers.find(
                                    (q: any) =>
                                      q.questionnaire.question ===
                                      "What's Your Body Type?",
                                  )?.selectedOptions[0]?.label || "N/A"}
                                </p>
                              </div>
                              <div className="border-b border-gray-300 pb-2">
                                <p className="text-xs text-gray-500">
                                  Hours of sleep:
                                </p>
                                <p className="text-sm font-medium text-gray-800">
                                  {user.questionnaireAnswers.find(
                                    (q: any) =>
                                      q.questionnaire.question ===
                                      "How Much Sleep Do You Get Each Night, On Average?",
                                  )?.selectedOptions[0]?.label || "N/A"}
                                </p>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </>
        )}
      </div>
      {errorMessage && (
        <ErrorMessage
          message={errorMessage}
          onClose={() => setErrorMessage(null)}
        />
      )}
    </div>
  );
};

export default UserDetails;
